import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookMarked, Search, Filter, Download, Power, Edit3, Check, Trash2, Clock } from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  entwurf:      "bg-cream-200 text-sand-700",
  geprueft:     "bg-saffron/25 text-clay-dark",
  freigegeben:  "bg-sage-dark/15 text-sage-dark",
};
const STATUS_LABELS = {
  entwurf: "Entwurf", geprueft: "Geprüft", freigegeben: "Freigegeben",
};

export default function CampusPflegeKiKb() {
  const { user } = useCampusAuth();
  const isAdmin = !!user?.is_admin;

  const [rules, setRules] = useState([]);
  const [bereiche, setBereiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBereich, setFilterBereich] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null);   // rule object or null
  const [history, setHistory] = useState(null);   // rule object or null

  useEffect(() => {
    document.title = "Pflege-KI · Wissensbibliothek · Dickies-Campus";
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: rs }, { data: be }] = await Promise.all([
        axios.get(`${API}/pflege-ki/kb/rules`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/kb/rules/bereiche`, { withCredentials: true }),
      ]);
      setRules(rs || []);
      setBereiche((be?.bereiche) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wissensbibliothek konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return rules.filter((r) => {
      if (filterBereich && r.bereich !== filterBereich) return false;
      if (filterStatus && r.status !== filterStatus) return false;
      if (activeOnly && !r.aktiv) return false;
      if (ql) {
        const blob = `${r.bereich} ${r.risiko} ${(r.sis_hinweise || []).join(" ")}`.toLowerCase();
        if (!blob.includes(ql)) return false;
      }
      return true;
    });
  }, [rules, filterBereich, filterStatus, activeOnly, q]);

  const toggleActive = async (rule) => {
    try {
      const { data } = await axios.post(
        `${API}/pflege-ki/kb/rules/${rule.id}/toggle-active`,
        {}, { withCredentials: true },
      );
      setRules((cur) => cur.map((r) => r.id === rule.id ? { ...r, aktiv: data.aktiv } : r));
      toast.success(data.aktiv ? "Regel aktiviert" : "Regel deaktiviert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktivierung fehlgeschlagen.");
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Regel '${rule.bereich} · ${rule.risiko}' wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/pflege-ki/kb/rules/${rule.id}`, { withCredentials: true });
      setRules((cur) => cur.filter((r) => r.id !== rule.id));
      toast.success("Regel gelöscht");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const updateRule = async (rule_id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/pflege-ki/kb/rules/${rule_id}`, patch, { withCredentials: true });
      setRules((cur) => cur.map((r) => r.id === rule_id ? data : r));
      toast.success("Regel aktualisiert");
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen.");
    }
  };

  const exportCsv = () => {
    window.open(`${API}/pflege-ki/kb/export.csv`, "_blank");
  };

  return (
    <div className="space-y-7" data-testid="pki-kb-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-clay-dark">
            Pflege-KI · Wissensbibliothek
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-sand-800 leading-tight mt-1">
            Pflegefachliche Regeln &amp; Qualitätscheck
          </h1>
          <p className="mt-2 text-sand-600 text-[14px] max-w-3xl leading-relaxed">
            Kuratierte Pflege-Regeln pro Bereich. Aktive Regeln werden für den deterministischen
            Qualitätscheck im Pflege-KI-Modul herangezogen. Bearbeiten ist Admin-Mitgliedern vorbehalten.
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline"
          data-testid="kb-export-csv"
          className="rounded-full border-clay h-10">
          <Download className="h-4 w-4 mr-1.5" strokeWidth={1.7} />
          CSV exportieren
        </Button>
      </div>

      {/* FILTER */}
      <div className="rounded-2xl border border-cream-300 bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-sand-500" strokeWidth={1.8} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suchen (Bereich / Risiko / SIS-Hinweis) …"
            data-testid="kb-filter-search"
            className="w-full h-10 rounded-full border border-cream-300 bg-cream-50 pl-9 pr-3 text-[13px] focus:outline-none focus:border-terra"
          />
        </div>
        <select
          value={filterBereich}
          onChange={(e) => setFilterBereich(e.target.value)}
          data-testid="kb-filter-bereich"
          className="h-10 rounded-full border border-cream-300 bg-cream-50 px-4 text-[13px] focus:outline-none focus:border-terra"
        >
          <option value="">Alle Bereiche</option>
          {bereiche.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="kb-filter-status"
          className="h-10 rounded-full border border-cream-300 bg-cream-50 px-4 text-[13px] focus:outline-none focus:border-terra"
        >
          <option value="">Alle Status</option>
          <option value="entwurf">Entwurf</option>
          <option value="geprueft">Geprüft</option>
          <option value="freigegeben">Freigegeben</option>
        </select>
        <label className="inline-flex items-center gap-2 text-[12.5px] text-sand-700 select-none cursor-pointer">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
            data-testid="kb-filter-active-only" />
          nur aktive
        </label>
        <span className="ml-auto text-[12px] uppercase tracking-[0.22em] font-bold text-sand-500">
          {filtered.length} / {rules.length} Regeln
        </span>
      </div>

      {/* TABLE */}
      <div className="rounded-2xl border border-cream-300 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px] min-w-[900px]" data-testid="kb-table">
            <thead className="bg-cream-50 border-b border-cream-300">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-sand-700">Bereich</th>
                <th className="text-left px-4 py-3 font-semibold text-sand-700">Risiko</th>
                <th className="text-left px-4 py-3 font-semibold text-sand-700">SIS-Hinweise</th>
                <th className="text-center px-3 py-3 font-semibold text-sand-700">Status</th>
                <th className="text-center px-3 py-3 font-semibold text-sand-700">Quali</th>
                <th className="text-center px-3 py-3 font-semibold text-sand-700">Plan</th>
                <th className="text-center px-3 py-3 font-semibold text-sand-700">Aktiv</th>
                <th className="text-right px-4 py-3 font-semibold text-sand-700">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-200">
              {loading && (
                <tr><td colSpan={8} className="py-10 text-center text-sand-500 text-[13px]">Lade Regeln …</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-sand-500 text-[13px]">Keine Regeln gefunden.</td></tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} data-testid={`kb-row-${r.id}`} className={r.aktiv ? "" : "opacity-50"}>
                  <td className="px-4 py-3 font-semibold text-sand-800">{r.bereich}</td>
                  <td className="px-4 py-3 text-sand-700">{r.risiko}</td>
                  <td className="px-4 py-3 text-sand-600 text-[12.5px] max-w-[280px]">
                    {(r.sis_hinweise || []).slice(0, 3).join(" · ")}
                    {(r.sis_hinweise || []).length > 3 ? " …" : ""}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-flex h-6 px-2.5 items-center rounded-full text-[10.5px] uppercase tracking-[0.18em] font-bold ${STATUS_STYLES[r.status] || ""}`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {r.use_for_qualitaetscheck ? <Check className="h-4 w-4 text-sage-dark mx-auto" strokeWidth={2.4} /> : <span className="text-sand-400">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {r.use_for_massnahmenplan ? <Check className="h-4 w-4 text-sage-dark mx-auto" strokeWidth={2.4} /> : <span className="text-sand-400">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => isAdmin && toggleActive(r)}
                      disabled={!isAdmin}
                      data-testid={`kb-toggle-${r.id}`}
                      title={isAdmin ? "Aktiv umschalten" : "Nur Admin"}
                      className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${r.aktiv ? "bg-sage-dark" : "bg-sand-300"} ${!isAdmin && "cursor-not-allowed opacity-60"}`}
                    >
                      <span className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform ${r.aktiv ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => setHistory(r)} title="Änderungsverlauf"
                        data-testid={`kb-history-${r.id}`}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-cream-100 text-sand-600">
                        <Clock className="h-4 w-4" strokeWidth={1.7} />
                      </button>
                      <button onClick={() => isAdmin && setEditing(r)} disabled={!isAdmin}
                        data-testid={`kb-edit-${r.id}`}
                        title={isAdmin ? "Bearbeiten" : "Nur Admin"}
                        className={`h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-cream-100 text-sand-600 ${!isAdmin && "cursor-not-allowed opacity-40"}`}>
                        <Edit3 className="h-4 w-4" strokeWidth={1.7} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => deleteRule(r)} title="Löschen"
                          data-testid={`kb-delete-${r.id}`}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-brand-rose/30 text-brand-darkred">
                          <Trash2 className="h-4 w-4" strokeWidth={1.7} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* INFO-BOX */}
      <div className="rounded-2xl border border-terra/25 bg-peach-50 p-5 text-[13px] text-sand-700 leading-relaxed flex items-start gap-3">
        <BookMarked className="h-5 w-5 text-terra shrink-0 mt-0.5" strokeWidth={1.7} />
        <div>
          <strong className="text-sand-800">So funktioniert es:</strong>{" "}
          Aktive, freigegebene Regeln mit „Quali"-Häkchen werden im Qualitätscheck-Tab
          des Pflege-KI-Moduls deterministisch geprüft — kein LLM-Aufruf, alles offline-evaluierbar.
          Jede KI-Empfehlung bleibt ein Entwurf. Eine pflegefachliche Freigabe ist immer erforderlich.
        </div>
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <RuleEditModal
          rule={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            const updated = await updateRule(editing.id, patch);
            if (updated) setEditing(null);
          }}
        />
      )}

      {/* HISTORY MODAL */}
      {history && (
        <HistoryModal rule={history} onClose={() => setHistory(null)} />
      )}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────
 *  Edit Modal
 * ────────────────────────────────────────────────────────────*/
function RuleEditModal({ rule, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    bereich: rule.bereich || "",
    risiko: rule.risiko || "",
    sis_hinweise: (rule.sis_hinweise || []).join("\n"),
    ressourcen: (rule.ressourcen || []).join("\n"),
    pflegeziele: (rule.pflegeziele || []).join("\n"),
    massnahmen: (rule.massnahmen || []).join("\n"),
    haeufigkeit: rule.haeufigkeit || "",
    beobachtung: (rule.beobachtung || []).join("\n"),
    evaluation: rule.evaluation || "",
    md_hinweis: rule.md_hinweis || "",
    qualitaetscheck_regel: rule.qualitaetscheck_regel || "",
    widerspruch_warnung: rule.widerspruch_warnung || "",
    status: rule.status || "entwurf",
    use_for_qualitaetscheck: !!rule.use_for_qualitaetscheck,
    use_for_massnahmenplan: !!rule.use_for_massnahmenplan,
  }));
  const [busy, setBusy] = useState(false);

  const toList = (str) => str.split("\n").map((l) => l.trim()).filter(Boolean);

  const submit = async () => {
    setBusy(true);
    await onSave({
      bereich: form.bereich,
      risiko: form.risiko,
      sis_hinweise: toList(form.sis_hinweise),
      ressourcen: toList(form.ressourcen),
      pflegeziele: toList(form.pflegeziele),
      massnahmen: toList(form.massnahmen),
      haeufigkeit: form.haeufigkeit,
      beobachtung: toList(form.beobachtung),
      evaluation: form.evaluation,
      md_hinweis: form.md_hinweis,
      qualitaetscheck_regel: form.qualitaetscheck_regel,
      widerspruch_warnung: form.widerspruch_warnung,
      status: form.status,
      use_for_qualitaetscheck: form.use_for_qualitaetscheck,
      use_for_massnahmenplan: form.use_for_massnahmenplan,
    });
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="kb-edit-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl border border-cream-300 max-w-2xl w-full p-7 my-10 shadow-xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              Wissensbibliothek · Regel bearbeiten
            </div>
            <h3 className="font-display text-2xl text-sand-800 mt-1">{form.bereich || "Neue Regel"}</h3>
          </div>
          <button onClick={onClose} className="text-sand-500 hover:text-sand-800 text-xl leading-none">✕</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Bereich" value={form.bereich} onChange={(v) => setForm({ ...form, bereich: v })} testid="kb-edit-bereich" />
          <Field label="Risiko / Pflegeproblem" value={form.risiko} onChange={(v) => setForm({ ...form, risiko: v })} testid="kb-edit-risiko" />
        </div>

        <Field label="Häufigkeit" value={form.haeufigkeit} onChange={(v) => setForm({ ...form, haeufigkeit: v })} testid="kb-edit-haeufigkeit" />
        <Field label="Evaluation" value={form.evaluation} onChange={(v) => setForm({ ...form, evaluation: v })} testid="kb-edit-evaluation" />

        <FieldArea label="SIS-Hinweise (1 pro Zeile)" value={form.sis_hinweise} onChange={(v) => setForm({ ...form, sis_hinweise: v })} rows={4} testid="kb-edit-sis" />
        <FieldArea label="Ressourcen" value={form.ressourcen} onChange={(v) => setForm({ ...form, ressourcen: v })} rows={3} testid="kb-edit-ressourcen" />
        <FieldArea label="Pflegeziele" value={form.pflegeziele} onChange={(v) => setForm({ ...form, pflegeziele: v })} rows={3} testid="kb-edit-ziele" />
        <FieldArea label="Maßnahmen" value={form.massnahmen} onChange={(v) => setForm({ ...form, massnahmen: v })} rows={5} testid="kb-edit-massnahmen" />
        <FieldArea label="Beobachtungskriterien" value={form.beobachtung} onChange={(v) => setForm({ ...form, beobachtung: v })} rows={3} testid="kb-edit-beobachtung" />

        <FieldArea label="MD-Hinweis" value={form.md_hinweis} onChange={(v) => setForm({ ...form, md_hinweis: v })} rows={2} testid="kb-edit-md" />
        <FieldArea label="Qualitätscheck-Regel" value={form.qualitaetscheck_regel} onChange={(v) => setForm({ ...form, qualitaetscheck_regel: v })} rows={2} testid="kb-edit-qcheck" />
        <FieldArea label="Widerspruch-Warnung" value={form.widerspruch_warnung} onChange={(v) => setForm({ ...form, widerspruch_warnung: v })} rows={2} testid="kb-edit-widerspruch" />

        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">Freigabestatus</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="kb-edit-status"
              className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13.5px] focus:outline-none focus:border-terra">
              <option value="entwurf">Entwurf</option>
              <option value="geprueft">Geprüft</option>
              <option value="freigegeben">Freigegeben</option>
            </select>
          </div>
          <div className="space-y-2 pt-6">
            <label className="inline-flex items-center gap-2 text-[13px] text-sand-700">
              <input type="checkbox" checked={form.use_for_qualitaetscheck}
                onChange={(e) => setForm({ ...form, use_for_qualitaetscheck: e.target.checked })}
                data-testid="kb-edit-use-quali" />
              für Qualitätscheck verwenden
            </label><br />
            <label className="inline-flex items-center gap-2 text-[13px] text-sand-700">
              <input type="checkbox" checked={form.use_for_massnahmenplan}
                onChange={(e) => setForm({ ...form, use_for_massnahmenplan: e.target.checked })}
                data-testid="kb-edit-use-plan" />
              für Maßnahmenplan verwenden
            </label>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button onClick={onClose} variant="outline" size="sm" className="h-10 rounded-full border-clay">
            Abbrechen
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="kb-edit-save"
            className="h-10 rounded-full bg-terra hover:bg-terra-dark text-white px-5">
            {busy ? "Speichere …" : "Speichern"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="mt-3">
      <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-10 rounded-xl border border-cream-300 bg-cream-50 px-3 text-[13.5px] focus:outline-none focus:border-terra" />
    </div>
  );
}

function FieldArea({ label, value, onChange, rows, testid }) {
  return (
    <div className="mt-3">
      <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1.5">{label}</label>
      <textarea rows={rows || 3} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2 text-[13px] focus:outline-none focus:border-terra font-mono leading-snug" />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
 *  History Modal
 * ────────────────────────────────────────────────────────────*/
function HistoryModal({ rule, onClose }) {
  const log = rule.change_log || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
      data-testid="kb-history-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl border border-cream-300 max-w-lg w-full p-7 shadow-xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
              Änderungsverlauf
            </div>
            <h3 className="font-display text-xl text-sand-800 mt-1">{rule.bereich} · {rule.risiko}</h3>
          </div>
          <button onClick={onClose} className="text-sand-500 hover:text-sand-800 text-xl leading-none">✕</button>
        </div>
        {log.length === 0 ? (
          <p className="text-sand-500 text-[13px]">Keine Einträge.</p>
        ) : (
          <ul className="space-y-3 max-h-[440px] overflow-y-auto pr-2">
            {[...log].reverse().map((e, i) => (
              <li key={i} className="rounded-2xl border border-cream-300 bg-cream-50 p-3">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] font-bold text-clay-dark">
                  <span>{e.action}</span>
                  <span className="text-sand-500">{e.ts ? new Date(e.ts).toLocaleString("de-DE") : ""}</span>
                </div>
                <div className="mt-1 text-[12.5px] text-sand-700">{e.summary}</div>
                <div className="mt-1 text-[11.5px] text-sand-500">{e.user}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
